'use client';

/**
 * DocumentsSection — file attachments for rail cards.
 *
 * Renders the documents list from FullProductDetail (and other entities
 * that carry document attachments). Each item is a pill with an icon
 * keyed by MIME type, a filename, and a download affordance. Items are
 * grouped by `role` (brochure / spec_sheet / installation_guide / ...)
 * with the role acting as a group header; items without a role fall
 * into an unlabeled group at the bottom.
 *
 * Backed by the new MediaNode schema (kind=document) once the
 * sync-egress migration populates :Media nodes; falls back to inline
 * URL strings in the meantime. Either way the data shape passed into
 * this component is the same — ProductDocument from the BFF response.
 *
 * Click behavior:
 *   - In the rail, click downloads (window.open in a new tab). The
 *     concertina overlay PDF-render path is a separate concern that
 *     ProductDetailCard handles by intercepting onClick.
 *
 * Auto-hides when items is null/empty.
 *
 * @layer presentation
 * @arch_component_type section
 * @arch_domain copilot / rail-insights
 */

import { useState } from 'react';
import { FileText, FileImage, FileVideo, FileAudio, FileType2, Download, Loader2 } from 'lucide-react';
import { text } from '@/lib/design/tokens';
import { cn } from '@/lib/utils/cn';
import { useMediaDownload } from '@/lib/api/hooks';
import type { ProductDocument } from '@/components/copilot/details/sections/types';

interface DocumentsSectionProps {
  items: ProductDocument[] | null | undefined;
  label?: string;
  onItemClick?: (doc: ProductDocument) => void;
}

// Role display labels — title-case the slug. Unknown roles render verbatim.
const ROLE_LABELS: Record<string, string> = {
  brochure: 'Brochure',
  spec_sheet: 'Spec sheet',
  installation_guide: 'Installation guide',
  cad_drawing: 'CAD drawing',
  source_pdf: 'Source PDF',
  primary: 'Primary',
  gallery: 'Gallery',
};

function roleLabel(role: string | null | undefined): string {
  if (!role) return '';
  return ROLE_LABELS[role] ?? role.replace(/_/g, ' ');
}

function iconForMime(mime: string | null | undefined) {
  if (!mime) return FileText;
  if (mime.startsWith('image/')) return FileImage;
  if (mime.startsWith('video/')) return FileVideo;
  if (mime.startsWith('audio/')) return FileAudio;
  if (mime === 'application/pdf') return FileType2;
  return FileText;
}

interface Group {
  role: string | null;
  items: ProductDocument[];
}

function groupByRole(items: ProductDocument[]): Group[] {
  const map = new Map<string, ProductDocument[]>();
  const noRole: ProductDocument[] = [];
  for (const item of items) {
    if (!item.role) {
      noRole.push(item);
      continue;
    }
    const bucket = map.get(item.role) ?? [];
    bucket.push(item);
    map.set(item.role, bucket);
  }
  // Sort within each bucket by sequence (assembler already does this,
  // but be defensive — schema-driven layouts shouldn't depend on
  // server-side order for correctness).
  const groups: Group[] = [];
  for (const [role, bucketItems] of map.entries()) {
    groups.push({ role, items: bucketItems.slice().sort((a, b) => a.sequence - b.sequence) });
  }
  // Stable role ordering — alphabetical is fine; intentional role
  // ordering can come later as a schema option if needed.
  groups.sort((a, b) => (a.role ?? '').localeCompare(b.role ?? ''));
  if (noRole.length) {
    groups.push({ role: null, items: noRole.slice().sort((a, b) => a.sequence - b.sequence) });
  }
  return groups;
}

export function DocumentsSection({ items, label = 'Documents', onItemClick }: DocumentsSectionProps) {
  const downloadMedia = useMediaDownload();
  const [pendingSid, setPendingSid] = useState<string | null>(null);

  if (!items || items.length === 0) return null;

  const groups = groupByRole(items);

  const handleClick = async (doc: ProductDocument, e: React.MouseEvent) => {
    e.preventDefault();

    // Caller override (e.g. PDF-viewer mode in the canvas overlay)
    // wins outright — they're taking responsibility for the click.
    if (onItemClick) {
      onItemClick(doc);
      return;
    }

    // Legacy fallback: documents that come from the inline-array path
    // (no :Media node, no semantic_id) can't go through the BFF — open
    // them in a new tab. Retires when all products have been re-synced
    // through the new Media-schema writer.
    if (!doc.semantic_id) {
      window.open(doc.url, '_blank', 'noopener,noreferrer');
      return;
    }

    // Primary path: mirrors useMcpActions.downloadMcp — strategy fetch,
    // base64 decode, Blob + same-origin click to trigger download.
    setPendingSid(doc.semantic_id);
    try {
      const result = await downloadMedia(doc.semantic_id, doc.name);
      if (!result.success) {
        console.warn('media_download declined:', result.error);
        window.open(doc.url, '_blank', 'noopener,noreferrer');
      }
    } catch (err) {
      console.error('media_download error:', err);
      window.open(doc.url, '_blank', 'noopener,noreferrer');
    } finally {
      setPendingSid(null);
    }
  };

  return (
    <div>
      <p className={text.eyebrow}>{label}</p>
      <div className="space-y-2">
        {groups.map((group, gi) => (
          <div key={`${group.role ?? '_'}-${gi}`} className="space-y-1">
            {group.role && (
              <p className={text.eyebrow}>
                {roleLabel(group.role)}
              </p>
            )}
            {group.items.map((doc, i) => {
              const Icon = iconForMime(doc.mime_type);
              return (
                <button
                  key={`${doc.semantic_id ?? doc.url}-${i}`}
                  type="button"
                  onClick={(e) => handleClick(doc, e)}
                  // Pending only counts for a doc that has a semantic_id.
                  // Legacy fallback (no Media node) takes the open-in-new-
                  // tab path, which is synchronous and never "pending" —
                  // without this guard `null === null` makes every legacy-
                  // fallback row render disabled by default.
                  disabled={!!doc.semantic_id && pendingSid === doc.semantic_id}
                  className="w-full text-left flex items-center gap-2 p-2 rounded-md bg-muted/30 border border-border hover:bg-muted/50 transition-colors group disabled:opacity-60 disabled:cursor-wait"
                  title={doc.name}
                >
                  <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />
                  <span className={cn(text.body, 'truncate flex-1')}>{doc.name}</span>
                  {!!doc.semantic_id && pendingSid === doc.semantic_id ? (
                    <Loader2 className="h-3.5 w-3.5 shrink-0 text-muted-foreground/60 animate-spin" />
                  ) : (
                    <Download className="h-3.5 w-3.5 shrink-0 text-muted-foreground/60 group-hover:text-muted-foreground transition-colors" />
                  )}
                </button>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}
